import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { useNavigate } from "react-router-dom";
import { Button } from "../Button";
import { Loader } from "../Loader";
import { ROUTES } from "../../config/routes";
import { useAuth } from "../../context/AuthContext";
import { AdminPermission } from "../../api/generated/models/AdminPermission";
import { UsersAdminService } from "../../api/generated/services/UsersAdminService";
import { AdminUserViewModel } from "../../api/generated/models/AdminUserViewModel";
import { AdminUserDetailsViewModel } from "../../api/generated/models/AdminUserDetailsViewModel";
import { ReactComponent as CloseIcon } from "../../assets/icons/x.svg";
import { formatCopenhagenDateTime } from "../../helpers/dateFilters";
import "../../api/openApiConfig";

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <div className="text-sm text-gray-500">{label}</div>
    <div className="break-all">{children}</div>
  </div>
);

const orDash = (value?: string | number | null) =>
  value === null || value === undefined || value === "" ? "-" : value;

const PLATFORM_LABELS: Record<string, string> = {
  ios: "iOS",
  android: "Android",
  native: "Mobile app",
  web: "Web",
};

// How the platform was derived. An exact match on the registration event needs no
// caveat; the fallbacks do, so the admin can tell a fact from an inference.
const PLATFORM_SOURCE_HINTS: Record<string, string> = {
  first_activity: "from first activity",
  person_property: "app vs. web only",
};

const RegistrationPlatform: React.FC<{
  platform?: string | null;
  source?: string | null;
}> = ({ platform, source }) => {
  if (!platform) {
    return (
      <span className="text-gray-400" title="No analytics data for this user">
        Unknown
      </span>
    );
  }
  const hint = source ? PLATFORM_SOURCE_HINTS[source] : undefined;
  return (
    <>
      {PLATFORM_LABELS[platform] ?? platform}
      {hint && <span className="text-xs text-gray-400 ml-1">({hint})</span>}
    </>
  );
};

const CopyableId: React.FC<{ id: string }> = ({ id }) => {
  const [copied, setCopied] = React.useState(false);
  return (
    <button
      type="button"
      onClick={() =>
        navigator.clipboard
          ?.writeText(id)
          .then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          })
          .catch(() => {})
      }
      title="Click to copy"
      className="font-mono text-xs text-gray-500 hover:text-gray-700"
    >
      {id}
      {copied && <span className="ml-2 text-green-600">Copied</span>}
    </button>
  );
};

type PendingAction = "delete" | "hardDelete" | null;

interface UserDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: AdminUserViewModel | null;
  onUserChanged: () => void; // parent refreshes the list
}

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
  isOpen,
  onClose,
  user,
  onUserChanged,
}) => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  // The Users permission opens this page; deleting a user stays Super Admin only.
  const canDelete = hasPermission(AdminPermission.SUPER_ADMIN);
  const [details, setDetails] = useState<AdminUserDetailsViewModel | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);

  const goToTransactions = () => {
    if (!user?.id) return;
    navigate(`${ROUTES.ADMIN_TRANSACTIONS()}?identity=${encodeURIComponent(user.id)}`);
  };

  useEffect(() => {
    if (!isOpen || !user?.id) return;
    let ignore = false;
    setPendingAction(null);
    setError(null);
    const run = async () => {
      try {
        setLoading(true);
        const response = await UsersAdminService.postApiUsersAdminGetUser({ userId: user.id });
        if (!ignore) setDetails(response);
      } catch (err) {
        if (!ignore) setError("Failed to load user details");
        console.error("Error fetching user details:", err);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    run();
    return () => {
      ignore = true;
    };
  }, [isOpen, user?.id]);

  const runAction = async () => {
    if (!user?.id || !pendingAction) return;
    try {
      setSubmitting(true);
      setError(null);
      if (pendingAction === "delete") {
        await UsersAdminService.postApiUsersAdminDeleteUser({ userId: user.id });
      } else {
        await UsersAdminService.postApiUsersAdminHardDeleteUser({ userId: user.id });
      }
      onUserChanged();
      onClose();
    } catch (err) {
      setError(pendingAction === "delete" ? "Failed to delete user" : "Failed to hard delete user");
      console.error("Error deleting user:", err);
    } finally {
      setSubmitting(false);
      setPendingAction(null);
    }
  };

  const isDeleted = user?.accountState === "Deleted";

  return (
    <Modal
      className="modal-content md:max-w-[700px]"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="User Details"
    >
      <div className="flex justify-between items-start mb-6">
        <h2 className="text-primary font-medium text-xl">User Details</h2>
        <Button onClick={onClose} variant="icon">
          <CloseIcon className="w-3 h-3" />
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader />
        </div>
      ) : (
        details && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="ID">
              {details.id ? <CopyableId id={details.id} /> : <span className="text-sm text-gray-500">-</span>}
            </Field>
            <Field label="Name">{orDash(details.name)}</Field>
            <Field label="Email">{orDash(details.email)}</Field>
            <Field label="Phone">{orDash(details.phoneNumber)}</Field>
            <Field label="Account State">{orDash(details.accountState)}</Field>
            <Field label="Role">{orDash(details.role)}</Field>
            <Field label="Birth Year">{orDash(details.birthYear)}</Field>
            <Field label="Gender">{orDash(details.gender)}</Field>
            <Field label="External ID">
              <span className="font-mono text-sm">{orDash(details.externalId)}</span>
            </Field>
            <Field label="OiSTER User ID">{orDash(details.oisterUserId)}</Field>
            <Field label="Balance">{orDash(details.balance)}</Field>
            <Field label="Registered">
              {formatCopenhagenDateTime(details.registeredAt as any)}
            </Field>
            <Field label="Registered From">
              <RegistrationPlatform
                platform={details.registrationPlatform}
                source={details.registrationPlatformSource}
              />
            </Field>
          </div>
        )
      )}

      {error && (
        <div className="text-red-500 text-sm bg-red-50 border border-red-200 p-3 rounded-lg mt-4">
          {error}
        </div>
      )}

      {pendingAction ? (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="mb-3 text-sm">
            {pendingAction === "delete"
              ? `Delete (soft) user ${user?.email}? The account is marked deleted and unsubscribed.`
              : `HARD delete user ${user?.email}? This permanently removes the account and cannot be undone.`}
          </p>
          <div className="flex gap-3">
            <Button variant="danger" onClick={runAction} disabled={submitting}>
              {submitting ? "Working..." : "Confirm"}
            </Button>
            <Button variant="outline" onClick={() => setPendingAction(null)} disabled={submitting}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex justify-between mt-6 pt-4 border-t border-gray-200">
          <div className="flex gap-3">
            {canDelete && !isDeleted && (
              <Button variant="danger" onClick={() => setPendingAction("delete")}>
                Delete
              </Button>
            )}
            {canDelete && (
              <Button variant="danger" onClick={() => setPendingAction("hardDelete")}>
                HARD Delete
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" disabled={!user?.id} onClick={goToTransactions}>
              Transactions
            </Button>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default UserDetailsModal;
